import { ensurePlusPrefix, removePlusFromBeginning } from 'src/consumer/helpers/utilFunctions'
import { isNoContactEmail } from 'src/shared/patient/noContactEmail'
import { ensureMobileHasCountryCode } from 'src/shared/patient/phoneCountry'
import { buildConsentPayload } from './patientConsentUtils'
import {
  buildDateOfBirthFromPatientAge,
  buildProvidedDateOfBirth,
  getPatientAgeFieldsFromPatientInfo,
} from './patientAgeUtils'

const DEFAULT_ATTENDANT = {
  name: '',
  primary: true,
  type: 'relative',
  mobile: '',
  email: '',
}

function trimToEmpty(value) {
  if (typeof value !== 'string') {
    return value ?? ''
  }
  return value.trim()
}

function trimToNull(value) {
  const trimmed = trimToEmpty(value)
  if (typeof trimmed === 'string' && trimmed.length === 0) {
    return null
  }
  return trimmed
}

function sanitizeStringArray(items = []) {
  if (!Array.isArray(items)) {
    return []
  }
  return items
    .map((item) => trimToEmpty(item))
    .filter((item) => typeof item === 'string' && item.length > 0)
}

function sanitizePhoneList(items = [], defaultCountryIso) {
  return sanitizeStringArray(items).map((item) =>
    ensureMobileHasCountryCode(removePlusFromBeginning(item), defaultCountryIso),
  )
}

function sanitizeTags(tags = []) {
  if (!Array.isArray(tags)) return []
  const seen = new Set()
  return tags
    .filter((tag) => tag && typeof tag.label === 'string' && tag.label.trim())
    .map((tag) => ({ ...tag, label: tag.label.trim() }))
    .filter((tag) => {
      const key = (tag.tagCode || tag.label).toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

export function cleanAttendantsArray(attendantsArray) {
  if (!Array.isArray(attendantsArray)) {
    return []
  }

  return attendantsArray
    .map((item) => {
      const cleaned = {
        ...item,
        name: trimToEmpty(item?.name),
      }
      // Contact fields are optional — trim when present, but keep absent keys absent
      // so attendants saved before contact support round-trip unchanged.
      if (typeof item?.mobile === 'string') {
        cleaned.mobile = item.mobile.trim()
      }
      if (typeof item?.email === 'string') {
        cleaned.email = item.email.trim()
      }
      return cleaned
    })
    .filter((item) => item?.name)
}

function buildPrimaryPatientProfile(patientDetails = {}) {
  if (patientDetails?.primaryPatientProfile?.patientId) {
    return patientDetails.primaryPatientProfile
  }

  if (
    patientDetails?.dependentRelation &&
    patientDetails?.relatedToExistingPatient?.id &&
    patientDetails?.relatedToExistingPatient?.userId
  ) {
    return {
      apiParam: 'primary-patient-profile',
      patientId: patientDetails.relatedToExistingPatient.id,
      mobile: patientDetails.relatedToExistingPatient.mobile,
      email: patientDetails.relatedToExistingPatient.email,
      userId: patientDetails.relatedToExistingPatient.userId,
      dependentRelation: patientDetails.dependentRelation,
    }
  }

  return null
}

function buildLocationPayload(patientDetails = {}) {
  const existingLocation =
    patientDetails?.location && typeof patientDetails.location === 'object'
      ? patientDetails.location
      : {}

  const address = trimToNull(patientDetails?.address ?? existingLocation?.address)
  const city = trimToNull(patientDetails?.city ?? existingLocation?.city)
  const pincode = trimToNull(patientDetails?.pincode ?? existingLocation?.pincode)
  const state = trimToNull(patientDetails?.state ?? existingLocation?.state)
  const country = trimToNull(patientDetails?.country ?? existingLocation?.country)
  const countryCode = trimToNull(patientDetails?.countryCode ?? existingLocation?.countryCode)

  const nextLocation = {
    ...existingLocation,
    address,
    city,
    pincode,
    state,
    country,
    countryCode,
  }

  const hasUsefulLocationValue = Object.values(nextLocation).some(
    (value) => value !== null && value !== undefined && value !== '',
  )

  return hasUsefulLocationValue ? nextLocation : undefined
}

export function normalizePatientInfoForForm(
  patientInfo = {},
  { defaultCommunicationLanguage = 'en', withDefaultAttendant = true } = {},
) {
  const location =
    patientInfo?.location && typeof patientInfo.location === 'object' ? patientInfo.location : {}
  const normalizedAttendants = cleanAttendantsArray(patientInfo?.attendants)
  const normalizedAgeFields = getPatientAgeFieldsFromPatientInfo(patientInfo)
  // The internal placeholder is not something to show or edit; remember it so the update
  // payload can send it back unchanged (it is the patient's login username).
  const hasNoContactEmail = isNoContactEmail(patientInfo?.email, { patient: patientInfo })

  return {
    ...patientInfo,
    name: patientInfo?.name || '',
    email: hasNoContactEmail ? '' : patientInfo?.email || '',
    internalNoContactEmail: hasNoContactEmail ? patientInfo.email : '',
    mobile: patientInfo?.mobile ? removePlusFromBeginning(patientInfo.mobile) : '',
    ...normalizedAgeFields,
    note: patientInfo?.note || patientInfo?.notes || '',
    referredBy: patientInfo?.referredBy || '',
    address: patientInfo?.address ?? location?.address ?? '',
    city: patientInfo?.city ?? location?.city ?? '',
    pincode: patientInfo?.pincode ?? location?.pincode ?? '',
    state: patientInfo?.state ?? location?.state ?? '',
    country: patientInfo?.country ?? location?.country ?? '',
    countryCode: patientInfo?.countryCode ?? location?.countryCode ?? '',
    tags: sanitizeTags(patientInfo?.tags),
    consents: Array.isArray(patientInfo?.consents) ? patientInfo.consents : [],
    consentDraft: null,
    attendants:
      normalizedAttendants.length > 0
        ? normalizedAttendants
        : withDefaultAttendant
          ? [DEFAULT_ATTENDANT]
          : [],
    altMobiles: sanitizePhoneList(patientInfo?.altMobiles),
    altEmails: sanitizeStringArray(patientInfo?.altEmails),
    communicationToAttendant: Boolean(patientInfo?.communicationToAttendant),
    defaultCommunicationLanguage:
      patientInfo?.defaultCommunicationLanguage || defaultCommunicationLanguage || 'en',
    uhId: patientInfo?.uhId || '',
    importData: {
      ...(patientInfo?.importData || {}),
      importedFromId: patientInfo?.importData?.importedFromId || '',
    },
  }
}

export function buildPatientUpsertPayload(
  patientDetails = {},
  {
    clinicId,
    clinicBrandId,
    mentorId,
    patientId,
    includePatientNumber = false,
    anonymous = false,
    consentMode,
    consentTakenVia,
  } = {},
) {
  const defaultCountryIso = patientDetails?.defaultCountryIso || 'IN'
  const rawMobile = trimToNull(patientDetails?.mobile)
  // A number typed without its country code gets the clinic's (India by default).
  const mobile = rawMobile
    ? ensureMobileHasCountryCode(removePlusFromBeginning(rawMobile), defaultCountryIso)
    : null
  const email = trimToNull(patientDetails?.email) || trimToNull(patientDetails?.internalNoContactEmail)
  const payload = {
    apiParam: 'PatientInfo',
    id: patientId || patientDetails?.id || undefined,
    clinicId: clinicId ?? patientDetails?.clinicId,
    clinicBrandId: clinicBrandId ?? patientDetails?.clinicBrandId,
    mentorId: mentorId ?? patientDetails?.mentorId,
    name: trimToEmpty(patientDetails?.name),
    email,
    mobile: mobile ? ensurePlusPrefix(mobile) : null,
    // dateOfBirth is always filled (actual DOB, else assumed from age); dobProvided only when
    // an actual date was entered, so readers can tell the two apart.
    dateOfBirth: buildDateOfBirthFromPatientAge(patientDetails),
    dobProvided: buildProvidedDateOfBirth(patientDetails),
    gender: patientDetails?.gender || null,
    note: trimToNull(patientDetails?.note),
    referredBy: trimToNull(patientDetails?.referredBy),
    attendants: cleanAttendantsArray(patientDetails?.attendants),
    altMobiles: sanitizePhoneList(patientDetails?.altMobiles, defaultCountryIso).map(ensurePlusPrefix),
    tags: sanitizeTags(patientDetails?.tags),
    consents: buildConsentPayload(patientDetails, { mode: consentMode, takenVia: consentTakenVia }),
    altEmails: sanitizeStringArray(patientDetails?.altEmails),
    communicationToAttendant: Boolean(patientDetails?.communicationToAttendant),
    defaultCommunicationLanguage: patientDetails?.defaultCommunicationLanguage || undefined,
    communicationToPref: patientDetails?.communicationToPref || undefined,
    uhId: trimToNull(patientDetails?.uhId),
    importData: patientDetails?.importData || undefined,
    anonymous:
      Boolean(anonymous) ||
      Boolean(patientDetails?.anonymous) ||
      Boolean(patientDetails?.isAnonymous) ||
      Boolean(patientDetails?.anonymousPatient),
    primaryPatientProfile: buildPrimaryPatientProfile(patientDetails) || undefined,
    location: buildLocationPayload(patientDetails),
  }

  if (includePatientNumber && patientDetails?.patientNumber) {
    payload.patientNumber = patientDetails.patientNumber
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  )
}
